import type { IBookingService, IBookingServicesQuery, ICompany, IIntegration, IService, IServiceCategory } from '@types'
import useApi from '@booking/composables/useApi'
import { useBookingCartStore } from '@booking/stores/bookingCart'
import { get3DomainName } from '@booking/utils/domains'
import { getCompanySettings } from '@booking/utils/getCompanySettings'
import { useMetrics } from '@booking/utils/useMetrics'
import { ServiceTypes } from '@types'

import { placeholderImage } from '@ui/components/image/imageSettings'
import { getEnrichedMainServices, isInventoryMainService, sortCategories, sortFiles } from '@utils/services'
import { getTariff } from '@utils/tariff'
import { defineStore } from 'pinia'
import { getLogoUrl } from '../utils/images'

function getDefaultServices() {
  return []
}

export const useCompanyStore = defineStore(
  'company',
  () => {
    const nuxtApp = useNuxtApp()
    const api = useApi()

    const companyInfo = reactive({
      id: 0,
      alias: '',
      settings: getCompanySettings(),
    })
    const integrations = ref<IIntegration[]>([])
    const company = ref<ICompany>({} as ICompany)
    const hasInventory = ref<boolean>()
    const services = ref<IService[]>(getDefaultServices())
    const hasBookableInventoryServices = ref(false)
    const servicesLoaded = ref(false)
    const serviceTimezonesById = ref<Record<number, string>>({})
    const inventoryServices = ref<IBookingService[]>([])
    const serviceCategories = ref<IServiceCategory[]>([])
    const serviceCategoriesLoaded = ref(false)
    let companyRequestId = 0
    let inventoryRequestId = 0

    const metrics = useMetrics()

    function isCurrentCompanyRequest(id: number, requestId: number) {
      const currentCompanyId = companyInfo.id || company.value.id
      return requestId === companyRequestId && (!currentCompanyId || currentCompanyId === id)
    }

    async function getCompanyIdByAlias(alias: string = get3DomainName()) {
      const cart = useBookingCartStore()
      if (!cart.companyAlias && companyInfo.alias) {
        cart.setCompany(companyInfo.alias, true)
      }
      if (companyInfo.alias !== alias) {
        const data = await api.clients.getId(alias).catch(async () => {
          company.value = { id: -1 } as ICompany
          throw await nuxtApp.runWithContext(() => showError({ statusCode: 404 }))
        })
        if (data?.id) {
          companyInfo.id = data.id
          companyInfo.alias = alias
        }
      }
      companyInfo.settings = getCompanySettings(companyInfo.id)
      cart.setCompany(alias)
      return companyInfo.id
    }

    async function getCompanyByAlias(alias: string = get3DomainName()) {
      const id = await getCompanyIdByAlias(alias)
      if (!id) {
        return
      }
      return fetchCompany(id)
    }

    function applyIntegrations() {
      metrics.init(integrations.value)
    }

    const hasTinkoffIntegration = ref<boolean>()
    function checkTinkoffIntegration(data: ICompany) {
      const tinkoff = data.merchantData
      hasTinkoffIntegration.value = !!tinkoff
      data.inn = tinkoff?.inn || data.inn
    }

    async function fetchCompany(id: number) {
      if (company.value.id !== id) {
        const requestId = ++companyRequestId
        company.value = {} as ICompany
        integrations.value = []
        services.value = getDefaultServices()
        hasBookableInventoryServices.value = false
        servicesLoaded.value = false
        hasInventory.value = undefined
        inventoryServices.value = []
        serviceTimezonesById.value = {}
        inventoryRequestId++
        serviceCategories.value = []
        serviceCategoriesLoaded.value = false
        placeholderImage.value = ''

        const data = await api.clients.get(id)
        if (data && isCurrentCompanyRequest(id, requestId)) {
          data.files = sortFiles(data.files || [])
          checkTinkoffIntegration(data)
          data._tariff = getTariff(data)

          data.logoUrl = getLogoUrl(data)

          company.value = data
          placeholderImage.value = data.logoUrl

          integrations.value = data.clientIntegrations || []
          applyIntegrations()
        }
      }

      return company.value
    }

    async function fetchServices(id: number) {
      const requestId = companyRequestId
      if (!servicesLoaded.value && isCurrentCompanyRequest(id, requestId)) {
        services.value = getDefaultServices()
        const data = await api.services.get(id)
        if (data && isCurrentCompanyRequest(id, requestId)) {
          hasInventory.value = data.some(
            service => service.type === ServiceTypes.Inventory && service.customerCanView && service.isValid,
          )
          serviceTimezonesById.value = Object.fromEntries(
            data.map(service => [service.id, service.addresses?.[0]?.timezone || '']),
          )
          hasBookableInventoryServices.value = data.some(
            service => isInventoryMainService(service) && service.isValid && service.customerCanView,
          )
          services.value = getEnrichedMainServices(data).filter(service => service.isValid)
          servicesLoaded.value = true
        }
      }

      return services.value
    }

    async function fetchInventoryServices(clientAlias: string, query: IBookingServicesQuery) {
      const requestId = ++inventoryRequestId
      if (!hasBookableInventoryServices.value) {
        inventoryServices.value = []
        return inventoryServices.value
      }
      const data = await api.bookingServices.list(clientAlias, query)

      if (requestId === inventoryRequestId && hasBookableInventoryServices.value) {
        inventoryServices.value = data || []
      }

      return inventoryServices.value
    }

    async function fetchServiceCategories(id: number) {
      const requestId = companyRequestId
      if (!serviceCategoriesLoaded.value && isCurrentCompanyRequest(id, requestId)) {
        const data = await api.serviceCategories.list(id, { includeServices: true })
        if (isCurrentCompanyRequest(id, requestId)) {
          serviceCategories.value = sortCategories(data || [])
          serviceCategoriesLoaded.value = true
        }
      }

      return serviceCategories.value
    }

    const isCompanyLoaded = computed(() => !!company.value.id)

    return {
      company,
      hasInventory,
      isCompanyLoaded,
      companyInfo,
      services,
      servicesLoaded,
      hasBookableInventoryServices,
      inventoryServices,
      serviceTimezonesById,
      serviceCategories,
      serviceCategoriesLoaded,
      integrations,
      metrics,
      getCompanyIdByAlias,
      fetchCompany,
      fetchServices,
      fetchInventoryServices,
      fetchServiceCategories,
      getCompanyByAlias,
      hasTinkoffIntegration,
    }
  },
  {
    persist: {
      storage: piniaPluginPersistedstate.cookies(),
      pick: ['companyInfo.id', 'companyInfo.alias'],
    },
  },
)
